import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Banner,
  Button,
  Card,
  Chip,
  DivergingBars,
  Donut,
  Kpi,
  Legend,
  Meter,
  RankedBars,
  StateBlock,
  TrendChart,
  cumulative,
  seriesColor,
} from '@w7/ui'
import { useOverview, useSession } from '@/app/queries'
import { useStaffMe } from '@/api/hooks/shared/useStaffMe'
import { canCreateDownline } from '@/app/staffAccess'
import { useDateRange } from '@/app/range'
import { AsOf, Amount, Money, MiniSpark, deltaOf } from '@/app/bits'
import { availableToGive, limitUsage } from '@/lib/book'
import { attentionTotal } from '@/api/map/overview'
import { inrMajorToDisplay, parseCurrencyType } from '@/lib/currency'
import { count, dayLabel, displayMoney, displayMoneyCompact, EMPTY, lll, money, percent } from '@/lib/format'
import { ROLE_LABEL } from '@/data/types'

/**
 * Overview — position first, detail later.
 *
 * Live (Keycloak): GET /agents/{id}/overview per OpenAPI. Sport / open-bet /
 * unmatched are contract gaps → Unavailable, never invented. Demo: fixtures.
 */
export function OverviewPage() {
  const { range } = useDateRange()
  const { data: session } = useSession()
  const staffMe = useStaffMe()
  const allowCreateDownline = staffMe.isSuccess && canCreateDownline(staffMe.data)
  // `useOverview` takes a whole `OverviewQuery` now, because the reports grew
  // scope/sport/game filters. The overview itself deliberately passes only the
  // range: it is the "am I OK?" screen for YOUR seat, and a scoped or
  // sport-filtered front door would answer a question nobody asked it.
  const { data, isLoading, refetch, dataUpdatedAt } = useOverview({ range })
  const navigate = useNavigate()

  const series = useMemo(() => {
    if (!data?.daily.length || data.overviewUnavailable) return null
    const categories = data.daily.map((d) => dayLabel(d.date))
    const net = data.daily.map((d) => d.netPnl)
    return {
      categories,
      net,
      cumulativeNet: cumulative(net),
      turnover: data.daily.map((d) => d.turnover),
    }
  }, [data])

  const account = data?.account ?? session?.account ?? null
  const ccy = account?.currencyType
  const creditMoney = (n: number | null | undefined, extra: Parameters<typeof money>[1] = {}) =>
    displayMoney(n, ccy, extra)
  const creditCompact = (n: number | null | undefined) => displayMoneyCompact(n, ccy)
  const bookMoney = (n: number | null | undefined, extra: Parameters<typeof money>[1] = {}) =>
    money(n, { ...extra, currency: ccy })
  const bookCompact = (n: number | null | undefined, symbol = true) =>
    n == null || Number.isNaN(n)
      ? EMPTY
      : displayMoneyCompact(inrMajorToDisplay(n, parseCurrencyType(ccy)), ccy, symbol)
  const analyticsDown = Boolean(data?.overviewUnavailable)
  // Prefer analytics exposure; fall back to hierarchy (soft 0 when projector down).
  const hierarchyExposure = account?.exposure ?? null
  const hierarchyHeadroom =
    account != null ? availableToGive(account.balance, account.exposure) : null
  const headroom =
    !account
      ? null
      : analyticsDown || data?.exposureStale
        ? hierarchyHeadroom
        : availableToGive(account.balance, data?.exposure ?? account.exposure)
  const exposureValue = analyticsDown
    ? hierarchyExposure
    : data?.exposureStale
      ? hierarchyExposure
      : (data?.exposure ?? account?.exposure ?? null)
  const usage =
    account && exposureValue !== null ? limitUsage(exposureValue, account.creditLimit) : 0

  const netDelta =
    data && !analyticsDown && (data.priorBookNet !== 0 || data.bookNet !== 0)
      ? deltaOf(data.bookNet, data.priorBookNet)
      : null
  const turnoverDelta =
    data && !analyticsDown && (data.priorTurnover !== 0 || data.turnover !== 0)
      ? deltaOf(data.turnover, data.priorTurnover)
      : null
  const margin = data && !analyticsDown && data.turnover > 0 ? data.bookNet / data.turnover : 0
  const attentionCount = data && !analyticsDown ? attentionTotal(data.attentionCounts) : 0
  const firstRun =
    !isLoading &&
    !analyticsDown &&
    data &&
    data.subtreeMembers === 0 &&
    data.turnover === 0 &&
    data.topAgents.length === 0

  const noData = 'No data found'

  return (
    <>
      <div className="w7-page-head">
        <div style={{ minWidth: 0 }}>
          <h1 className="w7-page-title">Overview</h1>
          <p className="w7-card-sub">
            {account ? `${account.username} · ${ROLE_LABEL[account.role]}` : EMPTY} · {range.label}
            {data?.live ? ' · Live' : data && !data.live ? ' · Demo' : ''}
          </p>
        </div>
        <div className="w7-row-wrap" style={{ marginLeft: 'auto' }}>
          <AsOf at={data?.asOf ?? (dataUpdatedAt || undefined)} polls />
        </div>
      </div>

      {analyticsDown ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <Banner
            tone="warn"
            title="Overview data is unavailable"
            actions={
              <Button size="sm" variant="secondary" icon="refresh" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          >
            {data?.loadError ??
              'Analytics did not respond. Each section below shows No data found — nothing is invented.'}
          </Banner>
        </div>
      ) : null}

      {data?.martStale && !analyticsDown ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <Banner tone="warn" title="Reporting data is stale">
            The warehouse mart behind this overview is older than its expected cadence. Figures still
            render, but treat them as labelled-stale until the next successful build.
          </Banner>
        </div>
      ) : null}

      {data?.exposureStale ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <Banner tone="warn" title="Exposure projection is stale">
            Open exposure is withheld while the projection is stale — pre-bet checks fail closed. Free
            to give still uses your ledger balance only when exposure is known.
          </Banner>
        </div>
      ) : null}

      {firstRun ? (
        <div style={{ marginBottom: 'var(--w7-s5)' }}>
          <StateBlock
            icon="overview"
            title="Your book is empty"
            actions={
              allowCreateDownline ? (
                <Button variant="primary" icon="plus" onClick={() => navigate('/create')}>
                  Add to downline
                </Button>
              ) : undefined
            }
          >
            Create an agent or member to start seeing turnover, P&amp;L and attention on this page.
          </StateBlock>
        </div>
      ) : null}

      {/* ---------------------------------------------------------- KPIs -- */}
      <div className="w7-grid w7-grid-6" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Kpi
          label="Live balance"
          value={!account ? EMPTY : creditCompact(account.balance)}
          exact={!account ? undefined : creditMoney(account.balance)}
          accent
          meta={
            !account
              ? noData
              : `${creditMoney(headroom ?? 0, { round: true })} free to give · less ${creditMoney(exposureValue ?? 0, { round: true })} exposure`
          }
        />
        <Kpi
          label="Total balance"
          value={!account ? EMPTY : creditCompact(account.creditLimit)}
          exact={!account ? undefined : creditMoney(account.creditLimit)}
          meta={!account ? noData : 'Credit line on this agent'}
        />
        <Kpi
          label="Distributed balance"
          value={!account ? EMPTY : creditCompact(account.distributedBalance)}
          exact={!account ? undefined : creditMoney(account.distributedBalance)}
          meta={
            !account
              ? noData
              : `${creditMoney(Math.max(0, account.creditLimit - account.distributedBalance), { round: true })} remaining to assign`
          }
        />
        <Kpi
          label="Open exposure"
          value={exposureValue === null ? EMPTY : creditCompact(exposureValue)}
          exact={exposureValue === null ? undefined : creditMoney(exposureValue)}
          tone={
            exposureValue === null ? 'neutral' : usage > 1 ? 'loss' : usage > 0.8 ? 'warn' : 'neutral'
          }
          meta={
            exposureValue === null || !account ? (
              noData
            ) : (
              <>
                <Meter value={usage} label={`${percent(usage, 0)} of credit used`} />
                {percent(usage, 0)} of limit · your share of player bets
              </>
            )
          }
        />
        <Kpi
          label="Net P&L"
          value={analyticsDown || !data ? EMPTY : bookCompact(data.bookNet)}
          exact={analyticsDown || !data ? undefined : bookMoney(data.bookNet)}
          tone={analyticsDown || !data ? 'neutral' : data.bookNet < 0 ? 'loss' : 'profit'}
          delta={netDelta?.text}
          deltaTone={netDelta?.tone}
          spark={!analyticsDown && series ? <MiniSpark values={series.cumulativeNet} /> : undefined}
          meta={analyticsDown ? noData : `${percent(margin, 2)} margin on turnover`}
        />
        <Kpi
          label="Turnover"
          value={analyticsDown || !data ? EMPTY : bookCompact(data.turnover)}
          exact={analyticsDown || !data ? undefined : bookMoney(data.turnover)}
          delta={turnoverDelta?.text}
          deltaTone={turnoverDelta?.tone}
          spark={!analyticsDown && series ? <MiniSpark values={series.turnover} /> : undefined}
          meta={
            analyticsDown
              ? noData
              : data?.subtreeMembers != null
                ? `${count(data.activeMembers)} active of ${count(data.subtreeMembers)} members`
                : data
                  ? `${count(data.activeMembers)} active members`
                  : undefined
          }
        />
        <Kpi
          label="Active members"
          value={analyticsDown || !data ? EMPTY : count(data.activeMembers)}
          meta={
            analyticsDown
              ? noData
              : data?.subtreeMembers != null
                ? `of ${count(data.subtreeMembers)} · ${count(Math.max(0, data.subtreeMembers - data.activeMembers))} idle this period`
                : data
                  ? 'Subtree size not returned by overview'
                  : undefined
          }
        />
        <Kpi
          label="Open bets"
          value={data?.openBets != null && !analyticsDown ? count(data.openBets) : EMPTY}
          meta={analyticsDown || data?.openPositionUnavailable ? noData : undefined}
        />
      </div>

      {/* ------------------------------------------------------ attention -- */}
      {attentionCount > 0 ? (
        <div style={{ marginBottom: 'var(--w7-s5)' }}>
          <Banner
            tone={
              (data?.attentionCounts.creditLimitNearlyUsed ?? 0) > 0 ||
              (data?.attentionItems.some((a) => a.severity === 'error') ?? false)
                ? 'error'
                : 'warn'
            }
            title={`${attentionCount} operational attention ${attentionCount === 1 ? 'signal' : 'signals'}`}
            actions={
              <Button size="sm" variant="secondary" onClick={() => navigate('/downline?attention=1')}>
                Review downline
              </Button>
            }
          >
            {[
              data!.attentionCounts.creditLimitNearlyUsed > 0
                ? `${data!.attentionCounts.creditLimitNearlyUsed} near credit limit`
                : null,
              data!.attentionCounts.dormant30d > 0
                ? `${data!.attentionCounts.dormant30d} dormant 30d+`
                : null,
              data!.attentionCounts.neverPlacedABet > 0
                ? `${data!.attentionCounts.neverPlacedABet} never placed a bet`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Banner>
        </div>
      ) : null}

      {/* -------------------------------------------------------- charts -- */}
      <div className="w7-grid w7-grid-3" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Card
          className="w7-span-2"
          title="Turnover and cumulative P&L"
          subtitle="Bars are daily matched volume; the line is book net across the period (from overview trend)"
        >
          {isLoading && !series && !analyticsDown ? (
            <div style={{ height: 250 }} aria-hidden />
          ) : series ? (
            <TrendChart
              label="Daily turnover with cumulative net profit and loss"
              categories={series.categories}
              height={250}
              format={(v) => bookCompact(v, false)}
              maxLabels={8}
              series={[
                {
                  id: 'turnover',
                  label: 'Turnover',
                  values: series.turnover,
                  kind: 'bar',
                  color: 'var(--w7-c8)',
                  axis: 'right',
                },
                {
                  id: 'cum',
                  label: 'Cumulative net P&L',
                  values: series.cumulativeNet,
                  kind: 'area',
                  color: 'var(--w7-c1)',
                },
              ]}
              formatRight={(v) => bookCompact(v, false)}
              emptyMessage="No settled activity in this period"
            />
          ) : (
            <StateBlock icon="pnl" title={noData}>
              {analyticsDown
                ? 'Overview trend could not be loaded.'
                : `No daily trend rows returned for ${range.label}.`}
            </StateBlock>
          )}
        </Card>

        <Card title="Where the volume is" subtitle="Matched turnover by sport">
          {data?.sportUnavailable ? (
            <StateBlock icon="sport" title={noData}>
              {analyticsDown
                ? 'Sport breakdown unavailable while overview is down.'
                : 'Sport / product / competition breakdowns are not on the analytics contract — markets carry id and name only until a catalogue exists.'}
            </StateBlock>
          ) : (
            <>
              <Donut
                label="Turnover by sport"
                height={190}
                slices={(data?.bySport ?? []).slice(0, 6).map((s) => ({
                  label: s.sport,
                  value: s.turnover,
                }))}
                format={(v) => bookMoney(v, { round: true })}
                centerValue={data ? bookCompact(data.turnover) : EMPTY}
                centerLabel="turnover"
                emptyMessage="No turnover in this period"
              />
              <Legend
                items={(data?.bySport ?? []).slice(0, 6).map((s, i) => ({
                  label: s.sport,
                  color: seriesColor(i),
                  value: bookCompact(s.turnover),
                }))}
              />
            </>
          )}
        </Card>
      </div>

      <div className="w7-grid w7-grid-2" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Card
          title="Daily net P&L"
          subtitle="Green above the line is a day the book won; red below is a day it paid out"
        >
          {isLoading && !series && !analyticsDown ? (
            <div style={{ height: 220 }} aria-hidden />
          ) : series ? (
            <DivergingBars
              label="Net profit and loss by day"
              categories={series.categories}
              values={series.net}
              cumulativeValues={series.cumulativeNet}
              height={220}
              format={(v) => bookCompact(v, false)}
              maxLabels={8}
              emptyMessage="Nothing settled in this period"
            />
          ) : (
            <StateBlock icon="pnl" title={noData}>
              {analyticsDown
                ? 'Daily net P&L could not be loaded.'
                : `No daily book-net series for ${range.label}.`}
            </StateBlock>
          )}
        </Card>

        <Card
          title="Your agents"
          subtitle="Direct downline by matched turnover this period (GET /turnover?groupBy=agent)"
        >
          {data?.agentsUnavailable ? (
            <StateBlock icon="downline" title={noData}>
              Agent turnover could not be loaded. Retry later or open Downline.
            </StateBlock>
          ) : (
            <RankedBars
              label="Agents by turnover"
              rows={(data?.topAgents ?? []).slice(0, 7).map((a) => ({
                label: a.account.username,
                value: a.turnover,
                note: `${a.members} mbr`,
                onClick: () => navigate(`/member/${a.account.id}`),
              }))}
              format={(v) => bookCompact(v)}
              emptyMessage="No agent turnover in this period"
            />
          )}
        </Card>
      </div>

      <div className="w7-grid w7-grid-2">
        <Card
          title="Needs attention"
          subtitle="Operational signals only — credit, dormancy and never-bet (no surveillance)"
          flush
        >
          {isLoading && !analyticsDown ? (
            <div style={{ padding: 'var(--w7-s5)' }} aria-hidden />
          ) : analyticsDown ? (
            <StateBlock icon="warning" title={noData}>
              Attention signals could not be loaded.
            </StateBlock>
          ) : attentionCount === 0 && (data?.attentionItems.length ?? 0) === 0 ? (
            <StateBlock icon="check" title="Nothing needs attention">
              Checked dormant 30d+, never placed a bet, and credit limit nearly used — none flagged
              for your subtree.
            </StateBlock>
          ) : data && data.attentionItems.length > 0 ? (
            <ul className="w7-list">
              {data.attentionItems.map((item, i) => (
                <li key={`${item.account.id}-${i}`}>
                  <Link to={`/member/${item.account.id}`} className="w7-list-row">
                    <Chip tone={item.severity === 'error' ? 'loss' : 'warn'} size="sm">
                      {item.severity === 'error' ? 'Blocking' : 'Watch'}
                    </Chip>
                    <span style={{ minWidth: 0 }}>
                      <b>{item.account.username}</b>
                      <span className="w7-dim" style={{ display: 'block', fontSize: 12 }}>
                        {item.reason}
                      </span>
                    </span>
                    <Money
                      value={item.account.exposure}
                      tone="neutral"
                      compact
                      currency={item.account.currencyType}
                      alreadyDisplay
                    />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="w7-list">
              {data!.attentionCounts.creditLimitNearlyUsed > 0 ? (
                <li>
                  <Link to="/downline?attention=1" className="w7-list-row">
                    <Chip tone="warn" size="sm">
                      Credit
                    </Chip>
                    <span style={{ minWidth: 0 }}>
                      <b>Credit limit nearly used</b>
                      <span className="w7-dim" style={{ display: 'block', fontSize: 12 }}>
                        {count(data!.attentionCounts.creditLimitNearlyUsed)} accounts — review in
                        downline
                      </span>
                    </span>
                  </Link>
                </li>
              ) : null}
              {data!.attentionCounts.dormant30d > 0 ? (
                <li>
                  <Link to="/downline?attention=1" className="w7-list-row">
                    <Chip tone="warn" size="sm">
                      Dormant
                    </Chip>
                    <span style={{ minWidth: 0 }}>
                      <b>Dormant 30 days+</b>
                      <span className="w7-dim" style={{ display: 'block', fontSize: 12 }}>
                        {count(data!.attentionCounts.dormant30d)} accounts
                      </span>
                    </span>
                  </Link>
                </li>
              ) : null}
              {data!.attentionCounts.neverPlacedABet > 0 ? (
                <li>
                  <Link to="/downline?attention=1" className="w7-list-row">
                    <Chip tone="info" size="sm">
                      Never bet
                    </Chip>
                    <span style={{ minWidth: 0 }}>
                      <b>Never placed a bet</b>
                      <span className="w7-dim" style={{ display: 'block', fontSize: 12 }}>
                        {count(data!.attentionCounts.neverPlacedABet)} accounts
                      </span>
                    </span>
                  </Link>
                </li>
              ) : null}
            </ul>
          )}
        </Card>

        <Card
          title="Recent credit movements"
          subtitle="Gives and takes from GET /transfers (latest page)"
          flush
        >
          {data?.transfersUnavailable ? (
            <StateBlock icon="transfers" title={noData}>
              Transfer statement could not be loaded for this range.
            </StateBlock>
          ) : (data?.recentTransfers.length ?? 0) === 0 ? (
            <StateBlock icon="transfers" title="No credit has moved yet">
              Gives and takes with your direct downline appear here when the transfers mart has rows.
            </StateBlock>
          ) : (
            <ul className="w7-list">
              {data!.recentTransfers.map((t) => {
                const outgoing = t.fromId === account?.id || t.direction === 'give'
                return (
                  <li key={t.id}>
                    <span className="w7-list-row">
                      <Chip
                        tone={outgoing ? 'info' : 'profit'}
                        size="sm"
                        icon={outgoing ? 'give' : 'take'}
                      >
                        {outgoing ? 'Gave' : 'Took'}
                      </Chip>
                      <span style={{ minWidth: 0 }}>
                        <b>{t.remark || (outgoing ? 'Credit given' : 'Credit taken')}</b>
                        <span className="w7-dim" style={{ display: 'block', fontSize: 12 }}>
                          {lll(t.at)} · ref {t.ref.slice(0, 12)}
                        </span>
                      </span>
                      <Amount value={t.amount} currency={account?.currencyType} alreadyDisplay />
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </Card>
      </div>
    </>
  )
}
